import useLangs from '../../context/LangsContext';
import { useTranslation } from 'react-i18next';
import { isPlatform, RefresherEventDetail } from '@ionic/react';
import useToast from '../../context/ToastContext/ToastContext';
import { useHistory } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { userSelector } from '../../redux/user/selector';
import React, { useCallback, useState } from 'react';
import { usePushNotifications } from '../../shared/context/PushNotificationsContext';
import { PushNotificationsIsDisabledBanner, Refresher, useEvent } from '@mindly/ui-components';
import { PublicRoutes } from '../../routes/navigation';
import { logout } from '../../redux/user/reducer';
import { loadUserThunk } from '../../redux/user/thunk';
import { fetchSubscriptions } from '../../redux/subscriptions/thunk';
import { fetchPromoCodes } from '../../redux/promoCodes/promoCodes.thunk';
import { fetchRatedConsultations } from '../../redux/consultationReviews/consultationsReviews.thunk';
import BuildInformation from '../../components/BuildInformation/BuildInformation';
import AddAvatarModal from '../../modals/AddAvatarModal';
import { resetSpecialist } from '../../redux/specialists/reducer';
import { fetchSpecialistsFilters, fetchSpecialistsList } from '../../redux/specialists/thunk';
import { ProfileSettingsListFeature } from '../../features/ProfileSettingsListFeature';
import { ProfileAvatarFeature } from '../../features/ProfileAvatarFeature';
import { MindlyStreamChatCapacitor } from '@mindly/stream-chat-capacitor';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { copyAndResetSavedFilters } from 'src/shared/utils/filters';
import useIntercomPlugin from 'src/shared/hooks/useIntercomPlugin';
import { useAuthFlow } from 'src/shared/hooks/useAuthFlow';
import { ChangeLanguageModalWithLanguageWorkInProgressFeature } from 'src/features/ChangeLanguageModalWithLanguageWorkInProgressFeature';

export function ProfileSettingsWidget() {
  const { triggerSignOutFlow } = useAuthFlow();
  const { t } = useTranslation(['profile', 'app']);

  const { showToast } = useToast();
  const history = useHistory();
  const dispatch = useDispatch();
  const { locale } = useLangs();
  const { clearUser } = useIntercomPlugin();

  const user = useSelector(userSelector);

  const [showChangeImageModal, setShowChangeImageModal] = useState(false);
  const [changeLanguageShow, setChangeLanguageShow] = useState(false);
  const pushNotificationsState = usePushNotifications();
  const isLocalizedAreasOfWorkByClientProblem = useFeatureIsOn('localized_areas_of_work_by_client_problem');

  const handleOnLogout = useCallback(async () => {
    try {
      await triggerSignOutFlow();
      await clearUser(locale);
      dispatch(logout());
      await MindlyStreamChatCapacitor.logoutChatUser();
      // delete utm params after logout to avoid rewrite them for possible new user
      localStorage.removeItem('utm_params');

      history.replace(PublicRoutes.AUTH);
    } catch (error) {
      showToast({ error });
    }
  }, []);

  const onRefreshClientProfile = useEvent(async (evt: CustomEvent<RefresherEventDetail>) => {
    if (!user?.id) return;

    try {
      await Promise.all([
        dispatch(loadUserThunk(user?.id)).unwrap(),
        dispatch(fetchSubscriptions(user?.id)).unwrap(),
        dispatch(
          fetchPromoCodes({
            client_id: user?.id,
          })
        ).unwrap(),
        dispatch(fetchRatedConsultations(user?.id)).unwrap(),
        pushNotificationsState.updatePermissions(),
      ]);
    } catch (error) {
      showToast({ error });
    } finally {
      evt.detail.complete();
    }
  });

  const clickChangeImageHandler = useCallback(() => {
    setShowChangeImageModal(true);
  }, []);

  const onSelectLanguage = useEvent(async (language: string) => {
    dispatch(resetSpecialist());
    copyAndResetSavedFilters(user?.app_lang, language, user?.id ?? '');
    await dispatch(fetchSpecialistsFilters({ t, isLocalizedAreasOfWorkByClientProblem })).unwrap();
    dispatch(fetchSpecialistsList(language));
  });

  return (
    <>
      <Refresher onIonRefresh={onRefreshClientProfile} disabled={false} />
      <ProfileAvatarFeature
        onPress={clickChangeImageHandler}
        avatar={user?.avatar}
        fullName={`${user?.display_name} ${user?.last_name ?? ''}`}
      />
      {pushNotificationsState.isShowPushNotificationWarning ? (
        <PushNotificationsIsDisabledBanner
          title={t('app:notification.banner.title')}
          description={t('app:notification.banner.description')}
          buttonText={t('app:notification.banner.button')}
          className="mb-4"
          onOpenNativeSettings={pushNotificationsState.openAppNativeSettings}
        />
      ) : null}
      <ProfileSettingsListFeature
        locale={locale}
        handleOnLogout={handleOnLogout}
        openLanguageModal={() => setChangeLanguageShow(true)}
      />
      <BuildInformation className={`${isPlatform('hybrid') ? '' : 'mb-7'}`} />
      <AddAvatarModal
        isOpen={showChangeImageModal}
        onDidDismiss={() => setShowChangeImageModal(false)}
        defaultImage={user?.avatar}
      />
      <ChangeLanguageModalWithLanguageWorkInProgressFeature
        isOpen={changeLanguageShow}
        closeModal={() => setChangeLanguageShow(false)}
        onSelect={onSelectLanguage}
      />
    </>
  );
}
